import React, { createContext, useContext, useEffect, useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import {
  startOfMonth,
  startOfDay,
  endOfDay,
  parseISO,
  formatISO,
  isValid,
  isAfter,
} from 'date-fns';
import { useHistory, useLocation } from 'react-router';
import { stringify } from 'query-string';

import storage from '@dashboard/services/storage/storage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import Select from '@dashboard/components/Form/components/Select';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CardContent from '@dashboard/components/CardContent';
import Card from '@dashboard/components/Card';
import Link from '@dashboard/components/Link';
import CardHeaderActions from '@dashboard/components/CardHeaderActions';
import Button from '@dashboard/components/Button';
import DateRangePicker from '@dashboard/components/DateRangePicker';

import {
  dateRanges,
  dateRangeUnits,
  getUnitsForDateRange,
  getDefaultUnitForDateRange,
} from './date-ranges';

import RecentPayments from './components/RecentPayments';
import RecentSubscriptions from './components/RecentSubscriptions';
import RecentCustomers from './components/RecentCustomers';
import ActiveSubscribers from './components/ActiveSubscribers';
import NewSales from './components/NewSales';
import Views from './components/Views';
import MonthlyRecurringRevenue from './components/MonthlyRecurringRevenue';
import Revenue from './components/Revenue.js';
import RecentSubmissions from './components/RecentSubmissions';
import OnboardingSteps from './components/OnboardingSteps';
import WhatsNew from './components/WhatsNew';

function checkHasStatisticsData(seller) {
  if (seller?.disabled) return false;

  const allowedPlanHandles = ['premium', 'launch', 'grow', 'scale'];
  const hasAllowedPlan = allowedPlanHandles.includes(seller?.plan?.handle);

  if (!hasAllowedPlan) {
    return false;
  }

  const allowedSubscriptionStatuses = ['active', 'trialing'];
  const hasAllowedSubscriptionStatus = allowedSubscriptionStatuses.includes(
    seller?.subscriptionStatus,
  );

  if (!hasAllowedSubscriptionStatus) {
    return false;
  }

  return true;
}

const StatisticsContext = createContext();

export const useStatisticsContext = () => useContext(StatisticsContext);

function getTimezoneOffset() {
  const offsetMinutesTotal = new Date().getTimezoneOffset();
  const offsetHours = Math.floor(Math.abs(offsetMinutesTotal) / 60);
  const offsetMinutes = Math.abs(offsetMinutesTotal) % 60;

  const timezoneOffset =
    (offsetMinutesTotal > 0 ? '-' : '+') +
    String(offsetHours).padStart(2, '0') +
    ':' +
    String(offsetMinutes).padStart(2, '0');

  return timezoneOffset;
}

export default function StatisticsDashboard() {
  const { seller } = useAuth();
  const history = useHistory();
  const location = useLocation();

  const query = new URLSearchParams(location.search);

  const [range, setRange] = useState(
    query.get('range') || storage.local.getItem('statistics-range') || 'monthToDate',
  );
  const [unit, setUnit] = useState(
    query.get('unit') || storage.local.getItem('statistics-unit') || dateRangeUnits.DAY,
  );
  const [customStartDate, setCustomStartDate] = useState(
    query.get('startDate') ? parseISO(query.get('startDate')) : null,
  );
  const [customEndDate, setCustomEndDate] = useState(
    query.get('endDate') ? parseISO(query.get('endDate')) : null,
  );

  useEffect(() => {
    storage.local.setItem('statistics-range', range);
  }, [range]);

  useEffect(() => {
    storage.local.setItem('statistics-unit', unit);
  }, [unit]);

  useEffect(() => {
    const params = {
      range,
      unit,
      ...(range === 'custom' && customStartDate
        ? { startDate: formatISO(customStartDate, { representation: 'date' }) }
        : {}),
      ...(range === 'custom' && customEndDate
        ? { endDate: formatISO(customEndDate, { representation: 'date' }) }
        : {}),
    };

    history.replace({ search: stringify(params) });
  }, [range, unit, customStartDate, customEndDate, history]);

  // Check if unit is still valid when custom dates change
  useEffect(() => {
    if (range === 'custom' && customStartDate && customEndDate) {
      const availableUnits = getUnitsForDateRange(
        startOfDay(customStartDate),
        endOfDay(customEndDate),
      );
      if (!availableUnits.includes(unit)) {
        setUnit(getDefaultUnitForDateRange(startOfDay(customStartDate), endOfDay(customEndDate)));
      }
    }
  }, [range, unit, customStartDate, customEndDate, setUnit]);

  let dateStart;
  let dateEnd;

  if (range === 'custom') {
    dateStart = customStartDate ? startOfDay(customStartDate) : startOfDay(new Date());
    dateEnd = customEndDate ? endOfDay(customEndDate) : endOfDay(new Date());
  } else {
    if (range === 'allTime' && seller?.createdAt) {
      dateStart = startOfMonth(new Date(seller.createdAt));
    } else if (
      range === 'currentBillingPeriod' &&
      seller?.stripeSubscriptionCurrentPeriodStart &&
      seller?.stripeSubscriptionCurrentPeriodEnd
    ) {
      // Use the actual subscription period dates directly
      dateStart = startOfDay(new Date(seller.stripeSubscriptionCurrentPeriodStart));
      dateEnd = endOfDay(new Date(seller.stripeSubscriptionCurrentPeriodEnd));
    } else {
      dateStart = dateRanges[range]?.start || startOfDay(new Date());
      dateEnd = dateRanges[range]?.end || endOfDay(new Date());
    }
  }

  // If we find ourselves with an invalid start date, reset to monthToDate start date
  if (!isValid(dateStart)) {
    dateStart = dateRanges.monthToDate.start;
  }

  // If we find ourselves with an invalid end date, reset to monthToDate end date
  if (!isValid(dateEnd)) {
    dateEnd = dateRanges.monthToDate.end;
  }

  // If the end date is before the start date, set the end date to the start date
  if (!isAfter(dateEnd, dateStart)) {
    dateEnd = dateStart;
  }

  const hasStatisticsData = checkHasStatisticsData(seller);

  function handleSetRange(range) {
    setRange(range);

    if (range === 'custom') {
      // For custom ranges, check if current unit is still valid
      const customUnits = getUnitsForDateRange(dateStart, dateEnd);
      if (!customUnits.includes(unit)) {
        setUnit(getDefaultUnitForDateRange(dateStart, dateEnd));
      }
    } else if (range === 'currentBillingPeriod') {
      // For billing period, ensure we use the correct dates
      if (
        seller?.stripeSubscriptionCurrentPeriodStart &&
        seller?.stripeSubscriptionCurrentPeriodEnd
      ) {
        const billingPeriodUnits = getUnitsForDateRange(
          startOfDay(new Date(seller.stripeSubscriptionCurrentPeriodStart)),
          endOfDay(new Date(seller.stripeSubscriptionCurrentPeriodEnd)),
        );
        if (!billingPeriodUnits.includes(unit)) {
          setUnit(
            getDefaultUnitForDateRange(
              startOfDay(new Date(seller.stripeSubscriptionCurrentPeriodStart)),
              endOfDay(new Date(seller.stripeSubscriptionCurrentPeriodEnd)),
            ),
          );
        }
      }
      // Clear custom dates when selecting a preset
      setCustomStartDate(null);
      setCustomEndDate(null);
    } else {
      const dateRange = dateRanges[range];
      if (dateRange && !dateRange.units.includes(unit)) {
        setUnit(dateRange.defaultUnit);
      }
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  }

  const timezoneOffset = getTimezoneOffset();

  return (
    <StatisticsContext.Provider
      value={{
        hasStatisticsData,
        range,
        unit,
        setRange: handleSetRange,
        setUnit,
        customStartDate,
        customEndDate,
        setCustomStartDate,
        setCustomEndDate,
        dateStart,
        dateEnd,
        timezoneOffset,
      }}
    >
      <Dashboard />
    </StatisticsContext.Provider>
  );
}

function Dashboard() {
  const { seller, hasActiveSubscription } = useAuth();
  const {
    range,
    unit,
    setUnit,
    setRange,
    dateStart,
    dateEnd,
    setCustomStartDate,
    setCustomEndDate,
  } = useStatisticsContext();

  // Get available units based on range
  const availableUnits =
    range === 'custom' ? getUnitsForDateRange(dateStart, dateEnd) : dateRanges[range]?.units || [];

  return (
    <Card variant="root">
      <CardHeader variant="root">
        <CardHeaderTitle variant="root">Your overview</CardHeaderTitle>

        <CardHeaderActions>
          <DateRangePicker
            value={range}
            onChange={setRange}
            startDate={dateStart}
            endDate={dateEnd}
            onDateChange={(start, end) => {
              setCustomStartDate(start);
              setCustomEndDate(end);
            }}
            className="w-auto"
            hasSubscription={hasActiveSubscription}
            billingPeriodStart={
              seller?.stripeSubscriptionCurrentPeriodStart
                ? new Date(seller.stripeSubscriptionCurrentPeriodStart)
                : null
            }
            billingPeriodEnd={
              seller?.stripeSubscriptionCurrentPeriodEnd
                ? new Date(seller.stripeSubscriptionCurrentPeriodEnd)
                : null
            }
          />

          <Select
            onChange={e => {
              setUnit(e.target.value);
            }}
            className="!w-28"
          >
            {availableUnits.includes(dateRangeUnits.HOUR) && (
              <option value={dateRangeUnits.HOUR} selected={unit === 'hour'}>
                Hourly
              </option>
            )}

            {availableUnits.includes(dateRangeUnits.DAY) && (
              <option value={dateRangeUnits.DAY} selected={unit === 'day'}>
                Daily
              </option>
            )}

            {availableUnits.includes(dateRangeUnits.WEEK) && (
              <option value={dateRangeUnits.WEEK} selected={unit === 'week'}>
                Weekly
              </option>
            )}

            {availableUnits.includes(dateRangeUnits.MONTH) && (
              <option value={dateRangeUnits.MONTH} selected={unit === 'month'}>
                Monthly
              </option>
            )}

            {availableUnits.includes(dateRangeUnits.YEAR) && (
              <option value={dateRangeUnits.YEAR} selected={unit === 'year'}>
                Yearly
              </option>
            )}
          </Select>

          <Button href="/pages/new" appearance="primary" className="whitespace-nowrap" wide>
            Create page
            <ArrowRightIcon className="w-4 h-4 ml-2 align-baseline" />
          </Button>
        </CardHeaderActions>
      </CardHeader>

      <OnboardingSteps />

      <WhatsNew />

      <div className="grid xl:grid-cols-6 2xl:grid-cols-9">
        <StatisticsCard type="chart">
          <Revenue />
        </StatisticsCard>

        <StatisticsCard type="chart">
          <MonthlyRecurringRevenue />
        </StatisticsCard>

        <StatisticsCard type="chart">
          <ActiveSubscribers />
        </StatisticsCard>

        <StatisticsCard type="chart">
          <NewSales />
        </StatisticsCard>

        <StatisticsCard type="chart">
          <Views />
        </StatisticsCard>

        <StatisticsCard type="list">
          <Card variant="none">
            <CardHeader variant="card">
              <CardHeaderTitle variant="card">
                <div className="text-base font-normal">New payments</div>
              </CardHeaderTitle>

              <div>
                <Link
                  href="/orders/payments"
                  className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                >
                  View all <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </div>
            </CardHeader>

            <CardContent variant="card">
              <RecentPayments />
            </CardContent>
          </Card>
        </StatisticsCard>

        <StatisticsCard type="list">
          <Card variant="none">
            <CardHeader variant="card">
              <CardHeaderTitle variant="card">
                <div className="text-base font-normal">New subscriptions</div>
              </CardHeaderTitle>
              <div>
                <Link
                  href="/orders/subscriptions"
                  className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                >
                  View all <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </div>
            </CardHeader>
            <CardContent variant="card">
              <RecentSubscriptions />
            </CardContent>
          </Card>
        </StatisticsCard>

        <StatisticsCard type="list">
          <Card variant="none">
            <CardHeader variant="card">
              <CardHeaderTitle variant="card">
                <div className="text-base font-normal">New submissions</div>
              </CardHeaderTitle>

              <div>
                <Link
                  href="/submissions"
                  className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                >
                  View all <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </div>
            </CardHeader>

            <CardContent variant="card">
              <RecentSubmissions />
            </CardContent>
          </Card>
        </StatisticsCard>

        <StatisticsCard type="list">
          <Card variant="none">
            <CardHeader variant="card">
              <CardHeaderTitle variant="card">
                <div className="text-base font-normal">New customers</div>
              </CardHeaderTitle>
              <div>
                <Link
                  href="/customers"
                  className="flex items-center gap-x-1 whitespace-nowrap text-sm"
                >
                  View all <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </div>
            </CardHeader>
            <CardContent variant="card">
              <RecentCustomers />
            </CardContent>
          </Card>
        </StatisticsCard>
      </div>
    </Card>
  );
}

function StatisticsCard({ children, type = 'chart' }) {
  const { hasStatisticsData } = useStatisticsContext();
  const { isAuthenticating } = useAuth();

  if (!hasStatisticsData && type === 'chart' && !isAuthenticating) {
    return (
      <div className={clsx('col-span-3 border-b border-r border-gray-100 p-4 py-8 relative')}>
        <div className="absolute inset-0 bg-white opacity-75 z-10 flex items-center justify-center">
          <div className="text-center text-gray-700">
            Start a subscription to see your statistics
          </div>
        </div>

        {children}
      </div>
    );
  }

  return (
    <div className={clsx('col-span-3 border-b border-r border-gray-100 p-4 py-8 min-h-[360px]')}>
      {children}
    </div>
  );
}
